import HomeIcon from "app/components/Icons/HomeIcon";
import { selectIsBoardLoading } from "app/redux/selector";
import { BoardType, IBoard } from "app/types";
import closeIcon from "assets/svgs/ic_close.svg";
import clsx from "clsx";
import { useQueryState } from "nuqs";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getBoardIconIcon } from "../../containers/Home/containers/helpers";
import { actions } from "../../redux/slice";
import { actions as appActions } from "../../../../../../redux/slice";
import { actions as actionsBoardSetting } from "../Settings/redux/slice";
import LotOfBoards from "./lot-of-board";
import CustomTooltip from "app/components/Tooltip";
import { CloseIcon } from "app/components/Icons";
import { selectDraftBoardForm } from "../Settings/redux/selector";

interface BoardNavMenuProps {
  boardData: IBoard[];
}

// Helper hook for zoom/resize
function useZoomResizeEffect(onZoomChange: (isZoomed: boolean) => void) {
  useEffect(() => {
    const handleResize = () => {
      const ratio = window.devicePixelRatio;
      if (ratio > 1.1) {
        // Zoomed above ~110% or more
        onZoomChange(true);
      } else {
        onZoomChange(false);
      }
    };

    window.addEventListener("resize", handleResize);

    // Also call it initially
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [onZoomChange]);
}

const Index: React.FC<BoardNavMenuProps> = ({ boardData }) => {
  const [settings, setSettings] = useQueryState("settings");
  const [options, setOptions] = useState(boardData);
  const [itemsToShow, setItemsToShow] = useState(5);
  const boardForm = useSelector(selectDraftBoardForm);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsBoardLoading);
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);

  useEffect(() => {
    if (!boardForm?._id) return;

    setOptions((prev) =>
      prev?.map((board) =>
        board._id === boardForm._id ? { ...board, name: boardForm.name } : board
      )
    );
  }, [boardForm.name, boardForm._id]);

  useEffect(() => {
    if (boardData) {
      const sorted = [...boardData].sort(
        (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
      );
      setOptions(sorted);
    }
  }, [boardData]);

  useEffect(() => {
    if (params.id) {
      setSelectedBoardId(params.id);
    } else {
      setSelectedBoardId(null);
    }
  }, [pathname, params]);

  const handleNavigate = (data: IBoard) => {
    setSettings(null);
    dispatch(actionsBoardSetting.getBoard({ id: data?._id }));
    dispatch(
      actions.doGetSelectedBoardData({
        id: data?._id,
        callback: () => {
          let newPath = "";
          if (data?.settings.Type === BoardType.Backlog) {
            newPath = `/app/${params.project}/${params.team}/boards/${data._id}/backlog`;
          } else {
            newPath = `/app/${params.project}/${params.team}/boards/${data._id}/${data?.settings?.viewType}/board`;
          }
          navigate(newPath);
        },
      })
    );
  };

  // 👉 Zoom and Resize Effect
  useZoomResizeEffect((isZoomed) => {
    if (isZoomed) {
      setItemsToShow(3); // when zoomed, show 3 boards
    } else {
      setItemsToShow(5); // normal zoom, show 5 boards
    }
  });

  const handleCloseTab = (val: string) => {
    dispatch(appActions.boardArchive({ id: val, callback() {} }));
  };

  return (
    <React.Fragment>
      <div className="flex flex-row bg-white shadow w-fit h-[42px]">
        <Link
          to={`/app/${params?.project}/${params?.team}/boards`}
          className={clsx(
            "relative flex flex-row items-center gap-2 px-4 uppercase text-sm font-normal text-secondary leading-5 py-2 hover:bg-gray-100 group",
            {
              "border-b-2 border-primary": !selectedBoardId,
            }
          )}
        >
          <HomeIcon
            size={20}
            color={!selectedBoardId ? "#5A8DF1" : "#79808A"}
          />
          <span
            className={clsx({
              "text-primary font-semibold": !selectedBoardId,
            })}
          >
            Home
          </span>
        </Link>

        {options?.slice(0, itemsToShow).map((option, index) => {
          return (
            <div
              key={index}
              className={clsx(
                "relative flex flex-row items-center gap-2 px-4 uppercase text-sm font-normal text-secondary leading-5 py-2 hover:bg-gray-100 group max-w-[308px] cursor-pointer",
                {
                  "border-b-2 border-primary": selectedBoardId === option._id,
                }
              )}
              onClick={() => handleNavigate(option)}
            >
              {getBoardIconIcon(option, selectedBoardId === option._id)}
              <CustomTooltip
                text={option?.name}
                position="bottom"
                className="truncate max-w-[80%]"
                contentClassName="max-w-[443px]"
              >
                <span
                  className={clsx("truncate ", {
                    "text-primary font-semibold":
                      selectedBoardId === option._id,
                  })}
                >
                  {option?.name}
                </span>
              </CustomTooltip>

              {/* Close Button with Tooltip */}
              {option?.settings?.Type !== BoardType.Backlog && (
                <CustomTooltip
                  text="Close tab"
                  contentClassName="capitalize"
                  position="bottom"
                >
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 bg-[#ECEEF2] rounded-md backdrop-blur-2xl hidden group-hover:flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseTab(option?._id);
                    }}
                  >
                    <CloseIcon color="#79808A" size="16" />
                  </button>
                </CustomTooltip>
              )}
            </div>
          );
        })}

        {options?.length > itemsToShow && (
          <LotOfBoards
            options={options?.slice(itemsToShow)}
            trigger={
              <button className="relative flex flex-row items-center gap-2 px-4 uppercase text-sm font-normal text-secondary leading-5 py-2 hover:bg-gray-100 group">
                {options?.length - itemsToShow}
                <span className="text-sm font-normal text-secondary ml-1">
                  more
                </span>
              </button>
            }
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default Index;
